import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { API_BASE_URL, ADMIN_API_BASE_URL, XMEDICAL_CONFIG } from '../../config';
import { SessionStorageService } from '../../storage';

/**
 * HTTP Interceptor that adds the Authorization bearer token and session ID to API requests.
 *
 * This interceptor is intended for applications that manage their own OAuth tokens
 * (e.g., with angular-oauth2-oidc).
 *
 * DO NOT use this interceptor if your auth library provides its own token interceptor
 * (e.g., angular-auth-oidc-client's authInterceptor).
 *
 * Headers added:
 * - `Authorization: Bearer <token>`
 * - `x-session-id`
 *
 * @example
 * // In app.module.ts (NgModule-based apps using angular-oauth2-oidc)
 * { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }
 */
@Injectable({
  providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {
  private baseUrl = inject(API_BASE_URL);
  private adminUrl = inject(ADMIN_API_BASE_URL);
  private config = inject(XMEDICAL_CONFIG);
  private sessionStorage = inject(SessionStorageService);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url.indexOf(this.adminUrl) > -1 || request.url.indexOf(this.baseUrl) > -1) {
      return this.addAuthHeaders(request, next);
    }
    return next.handle(request);
  }

  private addAuthHeaders(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.config.storageKeys) {
      throw new Error('XMedicalConfig.storageKeys is required but not provided');
    }

    const tokenKey = this.config.storageKeys.accessToken;
    const sessionKey = this.config.storageKeys.sessionId;

    const token = this.sessionStorage.getItem(tokenKey);
    const sessionId = this.sessionStorage.getItem(sessionKey);

    // Preserve existing headers
    const headerSettings: { [name: string]: string | string[]; } = {};
    for (const key of request.headers.keys()) {
      headerSettings[key] = request.headers.getAll(key);
    }

    // Add auth headers
    if (token) { headerSettings['Authorization'] = 'Bearer ' + token; }
    if (sessionId) { headerSettings['x-session-id'] = sessionId; }

    const newHeader = new HttpHeaders(headerSettings);
    const changedRequest = request.clone({ headers: newHeader });
    return next.handle(changedRequest);
  }
}
